import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil, fmtBRL, fmtNum } from '../utils';

export default function DistributionTab({ lang, year, petrobrasData, refDate, t, timelineData = { shareholders: [], dividends: [] } }) {
  const translateTerm = (term) => translateTermUtil(term, lang);

  const capRow = petrobrasData['distribuicao_capital']?.[0] || null;

  const divRows   = petrobrasData['distribuicao_dividendos'] || [];
  const divClRows = petrobrasData['distribuicao_dividendos_classe_acao'] || [];
  const divFallback = false;

  const fallbackNotice = () => null;

  const divPeriods = [...new Set(divRows.map(r => r.Data_Fim_Exercicio_Social))].sort();
  const latestDivPeriod = divPeriods[divPeriods.length - 1];
  const latestDiv = divRows.find(r => r.Data_Fim_Exercicio_Social === latestDivPeriod) || {};

  // DFP supplement: use when DFP data is more recent than FRE fallback
  const dfpDivRows = (timelineData.dividends || []).filter(r => r.source === 'dfp');
  const latestDfpRow = dfpDivRows.length > 0
      ? dfpDivRows.reduce((a, b) => (a.year > b.year ? a : b))
      : null;
  const freLatestYear = latestDivPeriod ? parseInt(String(latestDivPeriod).substring(0, 4), 10) : 0;
  const useDfpForKpis = latestDfpRow != null && latestDfpRow.year > freLatestYear;

  // Compute ROE for DFP rows using equity from dfpHistory (BPP_con account 2.03)
  let dfpRoe = null;
  if (latestDfpRow?.lucroAjustado) {
    const equityRow = (timelineData.dfpHistory || []).find(
      r => r.descricao === 'Patrimônio Líquido'
        && r.source === 'dfp'
        && r.period?.startsWith(String(latestDfpRow.year))
    );
    if (equityRow?.value) {
      dfpRoe = +((latestDfpRow.lucroAjustado / equityRow.value) * 100).toFixed(2);
    }
  }

  // Unified display object — maps DFP fields to the same shape as FRE latestDiv
  const displayDiv = useDfpForKpis ? {
      Data_Inicio_Exercicio_Social:                  latestDfpRow.divStart,
      Data_Fim_Exercicio_Social:                     latestDfpRow.divEnd,
      Data_Aprovacao_Retencao:                       null,
      Lucro_Liquido_Ajustado:                        latestDfpRow.lucroAjustado,
      Dividendo_Distribuido_Total:                   latestDfpRow.dividendoTotal,
      Dividendo_Distribuido_Lucro_Liquido_Ajustado:  latestDfpRow.payout,
      Taxa_Retorno_Patrimonio_Liquido_Emissor:       dfpRoe,
  } : latestDiv;
  const displayPeriod = useDfpForKpis ? latestDfpRow.divEnd : latestDivPeriod;

  const dividendChartData = (timelineData.dividends || [])
    .filter(p => p.dividendoTotal != null)
    .map(p => ({
      year: p.year,
      [lang === 'pt' ? 'Lucro Ajustado' : 'Adj. Net Income']: +(p.lucroAjustado / 1e9).toFixed(2),
      [lang === 'pt' ? 'Dividendos' : 'Dividends']: +(p.dividendoTotal / 1e9).toFixed(2),
      payout: +parseFloat(p.payout || 0).toFixed(1),
      period: p.divEnd,
    }));

  const shareholderChartData = (timelineData.shareholders || [])
    .filter(p => p.pf != null)
    .map(p => ({
      year: p.year,
      [lang === 'pt' ? 'Pessoa Física' : 'Individual']: Number(p.pf),
      [lang === 'pt' ? 'Pessoa Jurídica' : 'Corporate']: Number(p.pj),
      [lang === 'pt' ? 'Institucional' : 'Institutional']: Number(p.institucional),
      freeFloat: p.freeFloat,
    }));

  const DivTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[var(--card)] border border-[var(--border)] p-3 rounded-xl shadow-lg text-xs space-y-1.5">
        <p className="text-[var(--muted)] font-semibold">{lang === 'pt' ? `Ano de referência: ${label}` : `Filing year: ${label}`}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-bold">
            {p.name}: R$ {p.value}B
          </p>
        ))}
      </div>
    );
  };

  const ShareholderTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[var(--card)] border border-[var(--border)] p-3 rounded-xl shadow-lg text-xs space-y-1.5">
        <p className="text-[var(--muted)] font-semibold">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-bold">
            {p.name}: {Number(p.value).toLocaleString('pt-BR')}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">

      {/* Shareholder Composition */}
      <Card
        title={lang === 'pt' ? 'Composição do Quadro Acionário' : 'Shareholder Composition'}
        subtitle={lang === 'pt' ? `Última assembleia: ${capRow?.Data_Ultima_Assembleia || '-'}` : `Last assembly: ${capRow?.Data_Ultima_Assembleia || '-'}`}
        refDate={refDate}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: lang === 'pt' ? 'Acionistas PF' : 'Individual Shareholders', value: fmtNum(capRow?.Quantidade_Acionistas_PF), color: 'text-emerald-500' },
            { label: lang === 'pt' ? 'Acionistas PJ' : 'Corporate Shareholders', value: fmtNum(capRow?.Quantidade_Acionistas_PJ), color: 'text-blue-400' },
            { label: lang === 'pt' ? 'Investidores Institucionais' : 'Institutional Investors', value: fmtNum(capRow?.Quantidade_Acionistas_Investidores_Institucionais), color: 'text-yellow-500' },
            { label: lang === 'pt' ? 'Free Float Total' : 'Total Free Float', value: `${parseFloat(capRow?.Percentual_Total_Acoes_Circulacao || 0).toFixed(2)}%`, color: 'text-purple-400' },
          ].map((kpi, i) => (
            <div key={i} className="bg-[var(--accent-soft)]/30 border border-[var(--border)] rounded-xl p-3 space-y-1">
              <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block">{kpi.label}</span>
              <span className={`text-base font-black block ${kpi.color}`}>{kpi.value}</span>
            </div>
          ))}
        </div>

        <div>
          <p className="text-xs font-semibold text-[var(--muted)] mb-3 uppercase tracking-wider">
            {lang === 'pt' ? 'Evolução do Quadro Acionário (2015–2025)' : 'Shareholder Base Evolution (2015–2025)'}
          </p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={shareholderChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<ShareholderTooltip />} />
                <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                <Bar dataKey={lang === 'pt' ? 'Pessoa Física' : 'Individual'} stackId="a" fill="#10b981" radius={[0,0,0,0]} />
                <Bar dataKey={lang === 'pt' ? 'Pessoa Jurídica' : 'Corporate'} stackId="a" fill="#3b82f6" radius={[0,0,0,0]} />
                <Bar dataKey={lang === 'pt' ? 'Institucional' : 'Institutional'} stackId="a" fill="#f59e0b" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-[var(--border)]">
          {[
            { label: lang === 'pt' ? 'ON em Circulação' : 'Common Shares (Float)', qty: capRow?.Quantidade_Acoes_Ordinarias_Circulacao, pct: capRow?.Percentual_Acoes_Ordinarias_Circulacao, color: 'bg-emerald-500' },
            { label: lang === 'pt' ? 'PN em Circulação' : 'Preferred Shares (Float)', qty: capRow?.Quantidade_Acoes_Preferenciais_Circulacao, pct: capRow?.Percentual_Acoes_Preferenciais_Circulacao, color: 'bg-indigo-500' },
          ].map((item, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="flex items-center gap-1.5 font-semibold text-[var(--text)]">
                  <span className={`w-2 h-2 rounded-full ${item.color}`} />
                  {item.label}
                </span>
                <span className="font-bold text-yellow-500">{parseFloat(item.pct || 0).toFixed(2)}%</span>
              </div>
              <div className="text-[10px] text-[var(--muted)] font-mono">{fmtNum(item.qty)} {lang === 'pt' ? 'ações' : 'shares'}</div>
              <div className="w-full bg-[var(--accent-soft)] h-1.5 rounded-full">
                <div className={`${item.color} h-full rounded-full`} style={{ width: `${Math.min(parseFloat(item.pct || 0), 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Dividend History */}
      <Card title={lang === 'pt' ? 'Histórico de Dividendos' : 'Dividend History'} refDate={refDate}>
        {divFallback && fallbackNotice()}

        <div className="bg-[var(--accent-soft)]/20 border border-[var(--border)] rounded-xl px-4 py-2.5 mb-4 text-[10px] text-[var(--muted)] flex flex-wrap gap-x-6 gap-y-1">
          <span>{lang === 'pt' ? 'Exercício social:' : 'Fiscal year:'} <strong className="text-[var(--text)] font-mono">{displayDiv.Data_Inicio_Exercicio_Social} → {displayPeriod}</strong></span>
          <span>{lang === 'pt' ? 'AGM aprovação:' : 'AGM approval:'} <strong className="text-yellow-500 font-mono">{displayDiv.Data_Aprovacao_Retencao || '-'}</strong></span>
          <span className="text-[var(--muted)]/70">{lang === 'pt' ? 'Payout = Dividendos ÷ Lucro Ajustado · ROE = Lucro ÷ PL médio' : 'Payout = Dividends ÷ Adj. Income · ROE = Income ÷ Avg Equity'}</span>
          {useDfpForKpis && <span className="text-blue-400 font-semibold">{lang === 'pt' ? '★ Fonte: DFP (fluxo de caixa)' : '★ Source: DFP (cash flow)'}</span>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: lang === 'pt' ? 'Lucro Líq. Ajustado' : 'Adj. Net Income', value: fmtBRL(displayDiv.Lucro_Liquido_Ajustado), sub: lang === 'pt' ? 'base do payout' : 'payout base', color: 'text-emerald-500' },
            { label: lang === 'pt' ? 'Total Distribuído' : 'Total Distributed', value: fmtBRL(displayDiv.Dividendo_Distribuido_Total), sub: displayPeriod, color: 'text-blue-400' },
            { label: lang === 'pt' ? 'Payout' : 'Payout Ratio', value: `${parseFloat(displayDiv.Dividendo_Distribuido_Lucro_Liquido_Ajustado || 0).toFixed(1)}%`, sub: lang === 'pt' ? 'Dividendos ÷ Lucro Ajustado' : 'Dividends ÷ Adj. Income', color: 'text-yellow-500' },
            { label: 'ROE', value: displayDiv.Taxa_Retorno_Patrimonio_Liquido_Emissor != null ? `${parseFloat(displayDiv.Taxa_Retorno_Patrimonio_Liquido_Emissor).toFixed(2)}%` : '-', sub: lang === 'pt' ? 'Lucro ÷ Patrimônio Líquido' : 'Income ÷ Avg Equity', color: 'text-purple-400' },
          ].map((kpi, i) => (
            <div key={i} className="bg-[var(--accent-soft)]/30 border border-[var(--border)] rounded-xl p-3 space-y-1">
              <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block">{kpi.label}</span>
              <span className={`text-sm font-black block ${kpi.color}`}>{kpi.value}</span>
              <span className="text-[9px] text-[var(--muted)] font-mono block">{kpi.sub}</span>
            </div>
          ))}
        </div>

        <div>
          <p className="text-xs font-semibold text-[var(--muted)] mb-3 uppercase tracking-wider">
            {lang === 'pt' ? 'Lucro Ajustado vs Dividendos Distribuídos (R$ B)' : 'Adj. Net Income vs Dividends Paid (R$ B)'}
          </p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dividendChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `${v}B`} />
                <Tooltip content={<DivTooltip />} />
                <ReferenceLine y={0} stroke="var(--muted)" strokeDasharray="4 2" strokeOpacity={0.5} />
                <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                <Bar dataKey={lang === 'pt' ? 'Lucro Ajustado' : 'Adj. Net Income'} fill="#10b981" radius={[4,4,0,0]} />
                <Bar dataKey={lang === 'pt' ? 'Dividendos' : 'Dividends'} fill="#3b82f6" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {useDfpForKpis ? (
          <div className="mt-6 pt-4 border-t border-[var(--border)]">
            <p className="text-xs font-semibold text-[var(--muted)] mb-2 uppercase tracking-wider">
              {lang === 'pt' ? 'Detalhamento por Classe de Ação' : 'Breakdown by Share Class'}
            </p>
            <p className="text-xs text-[var(--muted)] italic py-2">
              {lang === 'pt'
                ? 'Detalhamento por classe indisponível — dados obtidos do Demonstrativo do Fluxo de Caixa (DFP).'
                : 'Per-class breakdown unavailable — data sourced from the Cash Flow Statement (DFP).'}
            </p>
          </div>
        ) : divClRows.length > 0 && (
          <div className="mt-6 pt-4 border-t border-[var(--border)]">
            <p className="text-xs font-semibold text-[var(--muted)] mb-3 uppercase tracking-wider">
              {lang === 'pt' ? 'Detalhamento por Classe de Ação' : 'Breakdown by Share Class'}
            </p>
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[600px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Período' : 'Period'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Espécie' : 'Share Type'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Tipo Dividendo' : 'Dividend Type'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Montante' : 'Amount'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Data Pagamento' : 'Payment Date'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {[...divClRows]
                    .sort((a, b) => String(b.Data_Fim_Exercicio_Social).localeCompare(String(a.Data_Fim_Exercicio_Social)))
                    .map((r, idx) => (
                    <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                      <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Fim_Exercicio_Social}</td>
                      <td className="p-3">
                        <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider border ${
                          r.Especie_Acao === 'Ordinária'
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                            : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400'
                        }`}>
                          {translateTerm(r.Especie_Acao)}
                        </span>
                      </td>
                      <td className="p-3 text-[var(--muted)]">{r.Dividendo_Distribuido || '-'}</td>
                      <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(r.Montante)}</td>
                      <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Pagamento_Dividendo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

    </div>
  );
}
